import { useState, useEffect, useRef, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { Play, Pause, Loader2 } from 'lucide-react'
import { getRecordingForEntry, type RecordingWithAudioUrl } from './api'

interface Props {
  entryId: string
}

function formatTime(seconds: number): string {
  const m = Math.floor(seconds / 60)
  const s = Math.floor(seconds % 60)
  return `${m}:${s.toString().padStart(2, '0')}`
}

export default function AudioPlayer({ entryId }: Props) {
  const { t } = useTranslation('recording')
  const [result, setResult] = useState<{ entryId: string; recording: RecordingWithAudioUrl | null; error: boolean } | null>(null)
  const [playing, setPlaying] = useState(false)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const progressRef = useRef<HTMLDivElement>(null)

  const loading = !result || result.entryId !== entryId
  const error = result?.entryId === entryId && result.error
  const recording = result?.entryId === entryId ? result.recording : null

  useEffect(() => {
    let cancelled = false
    getRecordingForEntry(entryId)
      .then((rec) => {
        if (!cancelled) setResult({ entryId, recording: rec, error: false })
      })
      .catch(() => {
        if (!cancelled) setResult({ entryId, recording: null, error: true })
      })
    return () => { cancelled = true }
  }, [entryId])

  useEffect(() => {
    if (!recording?.audioUrl) return
    const audio = new Audio(recording.audioUrl)
    audioRef.current = audio

    const onDuration = () => {
      if (Number.isFinite(audio.duration)) setDuration(audio.duration)
    }
    audio.addEventListener('loadedmetadata', onDuration)
    audio.addEventListener('durationchange', onDuration)
    audio.addEventListener('timeupdate', () => {
      setCurrentTime(audio.currentTime)
    })
    audio.addEventListener('ended', () => {
      setPlaying(false)
      setCurrentTime(0)
    })

    return () => {
      audio.pause()
      audio.src = ''
      audioRef.current = null
    }
  }, [recording?.audioUrl])

  const togglePlay = useCallback(() => {
    const audio = audioRef.current
    if (!audio) return
    if (playing) {
      audio.pause()
      setPlaying(false)
    } else {
      audio.play()
      setPlaying(true)
    }
  }, [playing])

  const handleSeek = useCallback((e: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>) => {
    const bar = progressRef.current
    const audio = audioRef.current
    if (!bar || !audio || !duration) return

    const rect = bar.getBoundingClientRect()
    const clientX = 'touches' in e ? (e as React.TouchEvent).touches[0]!.clientX : (e as React.MouseEvent).clientX
    const pct = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width))
    audio.currentTime = pct * duration
    setCurrentTime(audio.currentTime)
  }, [duration])

  if (loading) {
    return (
      <div className="flex items-center gap-3 bg-charcoal/60 border border-gold/10 rounded-2xl px-4 py-3 mb-4">
        <Loader2 size={18} className="text-gold/40 animate-spin" />
        <span className="text-pearl/60 text-sm">{t('playback.loading')}</span>
      </div>
    )
  }

  if (error || !recording) return null

  const validDuration = Number.isFinite(duration) && duration > 0
  const displayDuration = validDuration ? duration : (recording.durationSeconds ?? 0)
  const progress = displayDuration > 0 ? currentTime / displayDuration : 0

  return (
    <div className="flex items-center gap-3 bg-charcoal/60 border border-gold/10 rounded-2xl px-4 py-3 mb-4">
      {/* Play/Pause */}
      <button
        onClick={togglePlay}
        className="w-9 h-9 rounded-full bg-gold text-midnight flex items-center justify-center flex-shrink-0 hover:bg-gold-light transition-colors shadow-sm shadow-gold/20"
      >
        {playing
          ? <Pause size={16} fill="currentColor" />
          : <Play size={16} fill="currentColor" className="ml-0.5" />
        }
      </button>

      {/* Progress bar + times */}
      <div className="flex-1 min-w-0">
        <div
          ref={progressRef}
          onClick={handleSeek}
          onTouchStart={handleSeek}
          className="relative h-1.5 bg-slate-mid/40 rounded-full cursor-pointer group"
        >
          <div
            className="absolute inset-y-0 left-0 bg-gold rounded-full transition-[width] duration-100"
            style={{ width: `${progress * 100}%` }}
          />
          {/* Thumb */}
          <div
            className="absolute top-1/2 -translate-y-1/2 w-3 h-3 bg-gold rounded-full shadow-sm opacity-0 group-hover:opacity-100 transition-opacity"
            style={{ left: `calc(${progress * 100}% - 6px)` }}
          />
        </div>
        <div className="flex justify-between mt-1.5">
          <span className="text-pearl/60 text-[10px] font-mono">{formatTime(currentTime)}</span>
          <span className="text-pearl/60 text-[10px] font-mono">{formatTime(displayDuration)}</span>
        </div>
      </div>
    </div>
  )
}
